import { useContainerWidth } from "./flowsChartShared";

/**
 * The classic academic P/E valuation-band chart (2026-09-27): the real
 * post-2021 P/E path plotted against its own mean/±1σ/±2σ reference lines,
 * with cheap/fair/expensive colour zones behind it and today's reading
 * marked. Deliberately hand-rolled SVG rather than react-financial-charts
 * (Gate-0's mandated library for interactive OHLC/volume charts,
 * PriceChart.tsx/FlowsChart.tsx) -- this is a much simpler shape (one
 * line + STATIC horizontal reference bands + one highlighted point, no
 * crosshair/zoom/pan needed), and RFC has no first-class primitive for a
 * constant-value colour band; a small dedicated SVG component gets the
 * mean/±1σ/±2σ geometry exactly right with far less code than fighting
 * RFC's API for something it isn't designed to draw.
 *
 * ZONE BOUNDARIES MATCH THE SERVER'S OWN classification RULE EXACTLY
 * (index_valuation_view.py's `_classify`: cheap below mean-1σ, expensive
 * above mean+1σ, fair between) -- so the chip already shown in the text
 * summary and the zone the line sits in on this chart can never disagree.
 * ±2σ lines are drawn as an extra, fainter reference only (no third
 * colour) -- exactly as specified.
 */

export interface PeSeriesPoint {
  date: string;
  pe: number;
}

const HEIGHT = 240;
const MARGIN = { top: 12, right: 54, bottom: 22, left: 8 };

// Opacity picked so all three zones stay clearly distinguishable from each
// other (found live: 10% alpha made "fair" (amber) and "cheap" (green)
// look nearly identical on a white background -- amber needed MORE
// opacity than green/red to read as a distinct hue at this lightness).
const ZONE_COLORS = {
  cheap: "#1a9d6a26", // green ~15%
  fair: "#e8b02c40", // amber ~25% -- a brighter amber than the reference-line colour, needed extra opacity to read as yellow rather than washed-out grey
  expensive: "#c6413a26", // red ~15%
};

function formatTick(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
}

export function ValuationBandChart({
  series, meanPe, sdPe, currentPe, currentDate, classification,
}: {
  series: PeSeriesPoint[];
  meanPe: number | null;
  sdPe: number | null;
  currentPe: number | null;
  currentDate: string | null;
  classification: "cheap" | "fair" | "expensive" | null;
}) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();

  if (series.length < 2) {
    return <div ref={ref} className="empty-state" style={{ minHeight: 80 }}>Not enough history yet for a chart.</div>;
  }
  if (width === 0) {
    return <div ref={ref} style={{ minHeight: HEIGHT }} />;
  }

  const plotW = Math.max(width - MARGIN.left - MARGIN.right, 10);
  const plotH = HEIGHT - MARGIN.top - MARGIN.bottom;

  const peValues = series.map((p) => p.pe);
  const hasBand = meanPe !== null && sdPe !== null && sdPe > 0;
  const bandExtent = hasBand ? [meanPe! - 2.5 * sdPe!, meanPe! + 2.5 * sdPe!] : [Math.min(...peValues), Math.max(...peValues)];
  const yMin = Math.min(bandExtent[0], ...peValues);
  const yMax = Math.max(bandExtent[1], ...peValues);
  const yPad = (yMax - yMin) * 0.04 || 1;
  const domainMin = yMin - yPad;
  const domainMax = yMax + yPad;

  const xAt = (i: number) => MARGIN.left + (i / (series.length - 1)) * plotW;
  const yAt = (v: number) => MARGIN.top + (1 - (v - domainMin) / (domainMax - domainMin)) * plotH;

  const linePath = series.map((p, i) => `${i === 0 ? "M" : "L"}${xAt(i).toFixed(1)},${yAt(p.pe).toFixed(1)}`).join(" ");

  // X-axis: a handful of evenly-spaced ticks, real dates from the series
  // (this project's discontinuous-time convention -- see FlowsChart.tsx --
  // plots only dates actually present, never interpolated calendar ticks).
  const tickCount = Math.min(6, series.length);
  const tickIndices = Array.from({ length: tickCount }, (_, i) => Math.round((i / (tickCount - 1)) * (series.length - 1)));

  const lastIdx = series.length - 1;
  const todayX = xAt(lastIdx);
  const todayY = currentPe !== null ? yAt(currentPe) : yAt(series[lastIdx].pe);
  const todayColor = classification === "cheap" ? "#1a9d6a" : classification === "expensive" ? "#c6413a" : "#b8860b";

  return (
    <div ref={ref}>
      <svg width={width} height={HEIGHT} role="img" aria-label="P/E valuation band chart">
        {/* Colour zones, drawn first (behind everything) -- boundaries at
            exactly mean +/- 1sd, matching the server's own classification
            cut points. */}
        {hasBand && (
          <>
            <rect x={MARGIN.left} y={yAt(meanPe! + sdPe!)} width={plotW} height={Math.max(yAt(domainMin) - yAt(meanPe! + sdPe!), 0)} fill={ZONE_COLORS.cheap} />
            <rect x={MARGIN.left} y={yAt(meanPe! + sdPe!)} width={plotW} height={Math.max(yAt(meanPe! - sdPe!) - yAt(meanPe! + sdPe!), 0)} fill={ZONE_COLORS.fair} />
            <rect x={MARGIN.left} y={yAt(domainMax)} width={plotW} height={Math.max(yAt(meanPe! + sdPe!) - yAt(domainMax), 0)} fill={ZONE_COLORS.expensive} />
          </>
        )}

        {/* Reference lines: mean (solid), +/-1sd (dashed), +/-2sd (faint). */}
        {hasBand && (
          <>
            <line x1={MARGIN.left} x2={MARGIN.left + plotW} y1={yAt(meanPe!)} y2={yAt(meanPe!)} stroke="#8a91a0" strokeWidth={1.4} />
            <text x={MARGIN.left + plotW + 4} y={yAt(meanPe!) + 3} fontSize={9.5} fill="#8a91a0">mean {meanPe!.toFixed(1)}</text>

            <line x1={MARGIN.left} x2={MARGIN.left + plotW} y1={yAt(meanPe! + sdPe!)} y2={yAt(meanPe! + sdPe!)} stroke="#c6413a" strokeWidth={1} strokeDasharray="4 3" />
            <text x={MARGIN.left + plotW + 4} y={yAt(meanPe! + sdPe!) + 3} fontSize={9} fill="#c6413a">+1σ {(meanPe! + sdPe!).toFixed(1)}</text>
            <line x1={MARGIN.left} x2={MARGIN.left + plotW} y1={yAt(meanPe! - sdPe!)} y2={yAt(meanPe! - sdPe!)} stroke="#1a9d6a" strokeWidth={1} strokeDasharray="4 3" />
            <text x={MARGIN.left + plotW + 4} y={yAt(meanPe! - sdPe!) + 3} fontSize={9} fill="#1a9d6a">-1σ {(meanPe! - sdPe!).toFixed(1)}</text>

            <line x1={MARGIN.left} x2={MARGIN.left + plotW} y1={yAt(meanPe! + 2 * sdPe!)} y2={yAt(meanPe! + 2 * sdPe!)} stroke="#c6413a" strokeWidth={0.75} strokeDasharray="1 3" opacity={0.5} />
            <text x={MARGIN.left + plotW + 4} y={yAt(meanPe! + 2 * sdPe!) + 3} fontSize={8.5} fill="#c6413a" opacity={0.6}>+2σ</text>
            <line x1={MARGIN.left} x2={MARGIN.left + plotW} y1={yAt(meanPe! - 2 * sdPe!)} y2={yAt(meanPe! - 2 * sdPe!)} stroke="#1a9d6a" strokeWidth={0.75} strokeDasharray="1 3" opacity={0.5} />
            <text x={MARGIN.left + plotW + 4} y={yAt(meanPe! - 2 * sdPe!) + 3} fontSize={8.5} fill="#1a9d6a" opacity={0.6}>-2σ</text>
          </>
        )}

        {/* The P/E line itself, on top of the zones/reference lines. */}
        <path d={linePath} fill="none" stroke="#1a1d24" strokeWidth={1.5} />

        {/* Today's marker. */}
        <circle cx={todayX} cy={todayY} r={4} fill={todayColor} stroke="#fff" strokeWidth={1.2} />

        {/* X-axis tick labels. */}
        {tickIndices.map((i) => (
          <text key={i} x={xAt(i)} y={HEIGHT - 6} fontSize={9} fill="#8a91a0" textAnchor="middle">
            {formatTick(series[i].date)}
          </text>
        ))}
      </svg>
      {currentPe !== null && currentDate && (
        <div className="note" style={{ fontSize: "0.72rem", marginTop: 2 }}>
          <span style={{ color: todayColor, fontWeight: 700 }}>●</span> today ({currentDate}): {currentPe.toFixed(2)}×
        </div>
      )}
    </div>
  );
}
